import { action, observable, runInAction } from 'mobx';
import { BooleanStore, PasswordStore, StringStore } from 'ui/base/input';
import { CountryStore } from 'ui/standard/input/country_input/country_store';
import { IAccountsDomain } from 'services/accounts/types';
import { ResultKind } from 'services/base/types';
import * as UserService from 'services/accounts/user_service';
import { BusyStore } from 'ui/base/button';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { ModalStore } from 'ui/base/modal';
import { HistoryService } from 'services/base/history';
import { History, LocationState } from 'history';
import { loadAllPangolinDomains } from 'services/pangolin/loaders';
import { BasePath } from 'services/base/page_path';
import { MutableRefObject } from 'react';

export class RegisterStore {
  readonly email: StringStore;
  readonly password: PasswordStore;
  readonly country: CountryStore;
  readonly mobileNumber: StringStore;
  readonly termsAccepted: BooleanStore;
  readonly termsModal: ModalStore;

  @observable.ref
  emailError: string | undefined;

  @observable.ref
  passwordError: string | undefined;

  @observable.ref
  countryError: string | undefined;

  @observable.ref
  mobileNumberError: string | undefined;

  @observable.ref
  busy: BusyStore;

  constructor() {
    this.email = new StringStore('');
    this.password = new PasswordStore('');
    this.country = new CountryStore('United States', '+1');
    this.mobileNumber = new StringStore('');
    this.busy = new BusyStore();
    this.termsAccepted = new BooleanStore(false);
    this.termsModal = new ModalStore();
  }

  @action.bound
  async handleRegister(accounts: IAccountsDomain): Promise<UserService.CreateResult> {
    this.passwordError = undefined;
    const result = await UserService.post({
      domain: accounts,
      email: this.email.value,
      password: this.password.value,
      confirmPassword: this.password.value,
      country: this.country.country.value,
      mobileNumberPrefix: this.country.prefix.value,
      mobileNumber: this.mobileNumber.value,
      preferredLanguage: localeToLanguageCode(GlobalLocaleStore.get()),
    });
    return runInAction(() => {
      if (result.kind === ResultKind.USER_ERROR) {
        this.emailError = result.emailError;
        this.passwordError = result.passwordError;
        this.countryError = result.countryError;
        this.mobileNumberError = result.mobileNumberError;
      }
      return result;
    });
  }

  @action.bound
  async handleRedirects(result: UserService.CreateResult, accounts: IAccountsDomain, history: History<LocationState>) {
    if (result.kind === ResultKind.OK && accounts.user != null) {
      await loadAllPangolinDomains();
      const pangolin = accounts.pangolins.getDefault();
      if (accounts.user.emailVerifiedAt == null) {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.EMAIL_VERIFICATION,
        });
      } else if (pangolin == null) {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.ADD_PANGOLIN_SETUP,
        });
      } else {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.HOME,
          pangolinID: pangolin.id,
        });
      }
    }
  }

  @action.bound
  async registerOrShowTerms(accounts: IAccountsDomain, history: History<LocationState>, modalRef: MutableRefObject<HTMLDivElement | null>): Promise<UserService.CreateResult | undefined> {
    if (this.termsAccepted.value) {
      const busyResult = await this.busy.perform(async() => {
        const result = await this.handleRegister(accounts);
        await this.handleRedirects(result, accounts, history);
        return result;
      });
      return busyResult;
    } else {
      setTimeout(() => {
        if (modalRef.current != null) {
          modalRef.current.scrollTop = 0;
        }
      }, 0);
      this.termsModal.showModal();
      return undefined;
    }
  }

  @action.bound
  async acceptTermsThenRegister(accounts: IAccountsDomain, history: History<LocationState>): Promise<UserService.CreateResult> {
    this.termsAccepted.value = true;
    this.termsModal.hideModal();
    const busyResult = await this.busy.perform(async() => {
      const result = await this.handleRegister(accounts);
      await this.handleRedirects(result, accounts, history);
      return result;
    });
    return busyResult;
  }
}
